import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ShieldCheck, Inbox, ClipboardCheck, AlertOctagon, UserRound, Truck,
    Landmark, Play, ArrowUpRight, Info, TrendingUp, AlertTriangle, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import {
    LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
    PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import PageHeader from "../components/PageHeader";
import KpiCard from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import AlertBanner from "../components/AlertBanner";
import LoadingState from "../components/LoadingState";
import TechnicalStatusBadge from "../components/TechnicalStatusBadge";
import ReviewStatusBadge from "../components/ReviewStatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

function formatDT(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return iso; }
}

const STATUS_COLORS = {
    "PEC verificata": "#16A34A",
    "PEC verificata con avvertenze": "#F59E0B",
    "PEC anomala": "#DC2626",
    "Firma non valida": "#DC2626",
    "Certificato scaduto": "#F59E0B",
    "Busta di trasporto mancante": "#DC2626",
    "Busta di anomalia rilevata": "#F59E0B",
    "PEC non verificabile": "#64748B",
    "In elaborazione": "#38BDF8",
    "Formato in preparazione": "#94A3B8",
};

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user, companyId, isSuperAdmin, canAnalyze } = useAuth();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!companyId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        api.get("/dashboard/summary")
            .then((r) => setSummary(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Errore"))
            .finally(() => setLoading(false));
    }, [companyId]);

    if (loading) return <LoadingState label="Caricamento dashboard..." />;
    const kpis = summary?.kpis || {};
    const daily = summary?.daily || [];
    const distribution = (summary?.distribution || []).filter((d) => d.status && d.count > 0);
    const perCompany = summary?.per_company || [];
    const recent = summary?.recent_pec || [];
    const showAll = isSuperAdmin() && companyId === "__all__";

    return (
        <div className="space-y-6">
            <PageHeader
                title={`Benvenuto${user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}`}
                subtitle={showAll ? "Vista globale · tutte le aziende" : "Panoramica dello stato di sicurezza delle comunicazioni PEC."}
                actions={
                    canAnalyze() && !showAll && (
                        <Button
                            data-testid="analyze-new-pec-btn"
                            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-2 h-10"
                            onClick={() => navigate("/pec/upload")}
                        >
                            <Play className="w-4 h-4" />
                            Analizza nuova PEC
                        </Button>
                    )
                }
            />

            {error && (
                <AlertBanner variant="alto" title="Errore">{error}</AlertBanner>
            )}

            {showAll && (
                <AlertBanner
                    variant="info"
                    title="Modalità globale attiva"
                    testId="dashboard-global-mode-banner"
                >
                    Stai visualizzando dati aggregati di tutte le società. Seleziona una società
                    specifica dalla sidebar per operazioni mirate.
                </AlertBanner>
            )}

            {isSuperAdmin() && user && !user.mfa_enabled && !localStorage.getItem("ihpg_mfa_nudge_dismissed") && (
                <div data-testid="mfa-nudge-banner" className="bg-[#FEF3C7] border border-[#F59E0B]/40 rounded-lg px-4 py-3 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-[#78350F]">Attivazione MFA fortemente consigliata per Super Admin</div>
                        <div className="text-xs text-[#78350F]/80 mt-0.5">Il tuo account ha privilegi globali. Proteggilo con l'autenticazione a due fattori (TOTP).</div>
                    </div>
                    <button data-testid="mfa-nudge-cta" onClick={() => navigate("/settings")}
                            className="text-xs font-semibold text-[#78350F] hover:underline shrink-0 mt-0.5">
                        Attiva ora →
                    </button>
                    <button data-testid="mfa-nudge-dismiss" onClick={() => { localStorage.setItem("ihpg_mfa_nudge_dismissed", "1"); window.location.reload(); }}
                            className="text-xs text-[#78350F]/60 hover:text-[#78350F] shrink-0 mt-0.5 ml-2">
                        ✕
                    </button>
                </div>
            )}

            {/* Top KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard label="PEC oggi" value={kpis.pec_analyzed_today || 0} icon={Inbox} accent="blue" hint="Ultime 24 ore" testId="kpi-pec-today" />
                <KpiCard label="Tecnicamente valide" value={kpis.pec_verified || 0} icon={ShieldCheck} accent="green" testId="kpi-pec-verified" />
                <KpiCard label="Da verificare" value={(kpis.pec_with_warnings || 0) + (kpis.pec_anomalous || 0)} icon={AlertTriangle} accent="amber" testId="kpi-pec-to-verify" />
                <KpiCard label="Ad alto rischio" value={kpis.high_risk || 0} icon={AlertOctagon} accent="red" hint="Score < 40" testId="kpi-high-risk" />
                <KpiCard label="Critiche" value={kpis.critical || 0} icon={AlertOctagon} accent="red" hint="Score < 20" testId="kpi-critical" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <KpiCard label="Allegati sospetti" value={kpis.suspicious_attachments || 0} icon={Paperclip} accent="amber" hint="MIME mismatch o doppia estensione" testId="kpi-suspicious-att" />
                <KpiCard label="Nuovi mittenti 30gg" value={kpis.new_senders_30d || 0} icon={UserRound} accent="slate" testId="kpi-new-senders" />
                <KpiCard label="Revisioni aperte" value={kpis.reviews_open || 0} icon={ClipboardCheck} accent="amber" testId="kpi-reviews-open" />
                <KpiCard label="Var. IBAN" value={kpis.iban_change_requests ?? 0} icon={Landmark} accent="amber" hint="Regole IBAN — ultimi 30gg" testId="kpi-iban-changes" />
                <KpiCard label="T. medio revisione" value={"—"} icon={TrendingUp} accent="slate" hint="Fase successiva" testId="kpi-review-time" />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="p-5 border-[#DCE3EE] lg:col-span-2" data-testid="chart-daily">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-[#172033]">PEC analizzate — ultimi 30 giorni</h3>
                        <span className="text-xs text-[#64748B]">{daily.reduce((a, b) => a + b.count, 0)} totali</span>
                    </div>
                    <div style={{ width: "100%", height: 220 }}>
                        <ResponsiveContainer>
                            <LineChart data={daily}>
                                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => v?.slice(5)} />
                                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                                <RTooltip contentStyle={{ background: "white", border: "1px solid #DCE3EE", borderRadius: 8, fontSize: 12 }} />
                                <Line type="monotone" dataKey="count" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="p-5 border-[#DCE3EE]" data-testid="chart-distribution">
                    <h3 className="text-sm font-semibold text-[#172033] mb-4">Autenticità tecnica</h3>
                    {distribution.length === 0 ? (
                        <div className="text-sm text-[#64748B]">Nessun dato.</div>
                    ) : (
                        <div style={{ width: "100%", height: 220 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={distribution}
                                        dataKey="count"
                                        nameKey="status"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                    >
                                        {distribution.map((d, i) => (
                                            <Cell key={i} fill={STATUS_COLORS[d.status] || "#64748B"} />
                                        ))}
                                    </Pie>
                                    <RTooltip contentStyle={{ background: "white", border: "1px solid #DCE3EE", borderRadius: 8, fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    <div className="mt-2 space-y-1">
                        {distribution.slice(0, 5).map((d) => (
                            <div key={d.status} className="flex items-center gap-2 text-xs">
                                <span className="w-2 h-2 rounded-sm" style={{ background: STATUS_COLORS[d.status] || "#64748B" }} />
                                <span className="text-[#172033] flex-1 truncate">{d.status}</span>
                                <span className="text-[#64748B] font-mono">{d.count}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {showAll && perCompany.length > 0 && (
                <Card className="p-5 border-[#DCE3EE]" data-testid="chart-per-company">
                    <h3 className="text-sm font-semibold text-[#172033] mb-4">Messaggi per società</h3>
                    <div style={{ width: "100%", height: 220 }}>
                        <ResponsiveContainer>
                            <BarChart data={perCompany}>
                                <XAxis dataKey="company" tick={{ fontSize: 10, fill: "#64748B" }} />
                                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                                <RTooltip contentStyle={{ background: "white", border: "1px solid #DCE3EE", borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            )}

            {/* Recent PEC */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-[#172033]">Ultime PEC analizzate</h2>
                    <Button variant="ghost" size="sm" onClick={() => navigate("/pec/analyses")} data-testid="see-all-pec-btn">
                        Vedi tutte →
                    </Button>
                </div>
                {recent.length === 0 ? (
                    <EmptyState
                        icon={Inbox}
                        title="Nessuna PEC ancora analizzata"
                        description="Carica un file .eml per avviare la prima analisi."
                        action={
                            canAnalyze() && !showAll ? (
                                <Button variant="outline" className="gap-2" onClick={() => navigate("/pec/upload")}>
                                    Vai a nuova analisi
                                    <ArrowUpRight className="w-4 h-4" />
                                </Button>
                            ) : null
                        }
                    />
                ) : (
                    <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                        <table className="w-full text-sm" data-testid="recent-pec-table">
                            <thead>
                                <tr className="bg-[#F5F7FB] text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">
                                    <th className="px-4 py-3 text-left">Rischio</th>
                                    <th className="px-4 py-3 text-left">Oggetto</th>
                                    <th className="px-4 py-3 text-left">Mittente</th>
                                    <th className="px-4 py-3 text-left">Azienda</th>
                                    <th className="px-4 py-3 text-left">Autenticità tecnica</th>
                                    <th className="px-4 py-3 text-left">Data</th>
                                    <th className="px-4 py-3 text-left">Stato revisione</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.map((r) => {
                                    const risk = r.technical_score == null ? "—" :
                                        r.technical_score < 20 ? "Critico" :
                                            r.technical_score < 40 ? "Alto" :
                                                r.technical_score < 70 ? "Medio" : "Basso";
                                    const riskColor = risk === "Critico" || risk === "Alto" ? "#DC2626" :
                                        risk === "Medio" ? "#F59E0B" : risk === "Basso" ? "#16A34A" : "#94A3B8";
                                    return (
                                        <tr key={r.id} className="border-t border-[#DCE3EE] hover:bg-[#F5F7FB]/60 cursor-pointer" onClick={() => navigate(`/pec/analyses/${r.id}`)} data-testid={`recent-pec-row-${r.id}`}>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border" style={{ color: riskColor, borderColor: riskColor + "50", backgroundColor: riskColor + "10" }}>
                                                    {risk}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm font-medium text-[#172033] max-w-xs truncate">{r.subject || "(senza oggetto)"}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-[#64748B]">{r.from_address || "—"}</td>
                                            <td className="px-4 py-3 text-xs">{r.company_name || "—"}</td>
                                            <td className="px-4 py-3">
                                                <TechnicalStatusBadge status={r.technical_status || "Non verificabile"} />
                                                <div className="text-[10px] text-[#64748B] mt-0.5">Score {r.technical_score ?? "—"}/100</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-[#64748B]">{formatDT(r.date || r.created_at)}</td>
                                            <td className="px-4 py-3">
                                                <ReviewStatusBadge status={
                                                    r.review_status === "nuova" ? "Nuova" :
                                                        r.review_status === "in_revisione" ? "In revisione" :
                                                            r.review_status === "chiusa" ? "Chiusa" : "Nuova"
                                                } />
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/pec/analyses/${r.id}`); }}>
                                                    Apri
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
